import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { type: 'conference', title: '', description: '', speaker: '', time: '', location: '', order: 0 };
const TYPES = [
  { value: 'conference', label: 'Conférence' },
  { value: 'exposition', label: 'Exposition' },
  { value: 'atelier', label: 'Atelier' },
  { value: 'b2b', label: 'Rencontre B2B' },
];

export default function ManageProgramme() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/program`, { headers });
      setItems(res.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    if (!form.title || !form.description) { toast.error('Titre et description requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/program`, form, { headers });
      toast.success('Événement ajouté');
      setDialogOpen(false); setForm(emptyForm); fetchItems();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try { await axios.delete(`${API}/program/${id}`, { headers }); toast.success('Supprimé'); fetchItems(); }
    catch (e) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="manage-programme">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Programme</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} événement(s)</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Intervenant</TableHead>
                <TableHead>Horaire</TableHead>
                <TableHead>Lieu</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell><span className="text-xs px-2 py-1 rounded-full bg-[#F0F2F5] capitalize">{item.type}</span></TableCell>
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell className="text-sm text-[#6B7C93]">{item.speaker || '-'}</TableCell>
                  <TableCell className="text-sm">{item.time}</TableCell>
                  <TableCell className="text-sm">{item.location || '-'}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]"><Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucun événement</p></div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Ajouter un événement</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Intervenant</Label><Input value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} /></div>
              <div><Label>Horaire</Label><Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="09:00 - 10:30" /></div>
            </div>
            <div><Label>Lieu</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="event-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Ajouter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
